import { useState, useEffect, useCallback, useMemo, useRef } from "react"
import { useNavigate } from "react-router"
import AppLayout from "../../components/layout/AppLayout"
import { colors, getAccountChartColor } from "../../lib/colors"
import StatCard from "../../components/analytics/StatCard"
import LineChart from "../../components/analytics/LineChart"
import PostGrid from "../../components/analytics/PostGrid"
import SummaryTable from "../../components/analytics/SummaryTable"
import PeriodSelectorModal from "../../components/analytics/PeriodSelectorModal"
import { readStoredPeriods, presetToRange } from "../../lib/dateRange"
import type { PeriodEntry } from "../../lib/dateRange"
import { api } from "../../lib/api"
import type { FeedPostsData, FeedPostRow, ChartData, PostThumbnail } from "../../types/analytics"
import { useSelectedAccount } from "../../contexts/AccountContext"
import AccountSelectionNotice from "../../components/account/AccountSelectionNotice"
import FreeWindowNotice from "../../components/account/FreeWindowNotice"
import { useEarliestAccountDate } from "../../hooks/useEarliestAccountDate"
import { PAGE_TITLES } from "../../lib/navItems"
import { downloadCsv, downloadChartAsPng, chartDataToCsvRows, todayStr } from "../../lib/csv"
import "../../styles/analytics.scss"

const STORAGE_KEY = "lyto.feedPeriod"

type FeedFilter = "all" | "image" | "video" | "carousel"

const FILTER_LABELS: { key: FeedFilter; label: string }[] = [
  { key: "all",      label: "すべて" },
  { key: "image",    label: "画像" },
  { key: "video",    label: "動画" },
  { key: "carousel", label: "カルーセル" },
]

const SUMMARY_COLUMNS = [
  { key: "posts",          label: "投稿数" },
  { key: "frequency",      label: "投稿頻度" },
  { key: "likes",          label: "いいね" },
  { key: "comments",       label: "コメント" },
  { key: "saves",          label: "保存" },
  { key: "shares",         label: "シェア" },
  { key: "reach",          label: "リーチ" },
  { key: "engagement_rate", label: "ENG率" },
]

const fmtDateRange = (p: PeriodEntry) =>
  `${p.startDate.replace(/-/g, "/")} 〜 ${p.endDate.replace(/-/g, "/")}`

const FeedPostsPage = () => {
  const { selectedAccountIds, organizations, setSingleAccountOnly } = useSelectedAccount()
  const earliestDate = useEarliestAccountDate()
  const navigate = useNavigate()
  const reachChartRef      = useRef<HTMLDivElement>(null)
  const engagementChartRef = useRef<HTMLDivElement>(null)

  const freeWindowAccount = selectedAccountIds
    .map(id => organizations.flatMap(o => o.accounts).find(a => a.id === id))
    .find(a => a?.plan === "free")

  const accountColorMap = useMemo(() => {
    const map: Record<string, string> = {}
    selectedAccountIds.forEach((id, i) => {
      const account = organizations.flatMap(o => o.accounts).find(a => a.id === id)
      if (account) map[account.username] = getAccountChartColor(i).border
    })
    return map
  }, [selectedAccountIds, organizations])

  const applyAccountColors = (chartData: ChartData): ChartData => ({
    ...chartData,
    datasets: chartData.datasets.map((ds, i) => {
      const c = getAccountChartColor(i)
      return { ...ds, borderColor: ds.borderColor ?? c.border, backgroundColor: ds.backgroundColor ?? c.bg }
    }),
  })

  const applyPostColors = (posts: PostThumbnail[] | null | undefined) =>
    posts?.map(p => ({ ...p, color: p.username ? accountColorMap[p.username] : undefined })) ?? null

  const [dataByPeriod, setDataByPeriod] = useState<(FeedPostsData | null)[]>([null])
  const [loading, setLoading]           = useState(true)
  const [filter, setFilter]             = useState<FeedFilter>("all")
  const [periods, setPeriods]           = useState<PeriodEntry[]>(() => readStoredPeriods(STORAGE_KEY))
  const [refreshingId, setRefreshingId] = useState<number | null>(null)

  useEffect(() => {
    if (!earliestDate) return
    setPeriods(prev => {
      if (prev[0]?.preset !== "all") return prev
      return [{ ...prev[0], ...presetToRange("all", earliestDate) }, ...prev.slice(1)]
    })
  }, [earliestDate])

  useEffect(() => {
    setSingleAccountOnly(periods.length > 1)
  }, [periods, setSingleAccountOnly])

  const fetchData = useCallback(async () => {
    if (selectedAccountIds.length === 0) { setLoading(false); return }
    setLoading(true)
    try {
      const results = await Promise.all(
        periods.map(p => {
          const params = new URLSearchParams({ start_date: p.startDate, end_date: p.endDate })
          selectedAccountIds.forEach(id => params.append("instagram_account_ids[]", String(id)))
          if (filter !== "all") params.set("filter", filter)
          return api.get<FeedPostsData>(`/analytics/feed_posts?${params}`).catch(() => null)
        })
      )
      setDataByPeriod(results)
    } catch {
    } finally {
      setLoading(false)
    }
  }, [selectedAccountIds, periods, filter])

  useEffect(() => { fetchData() }, [fetchData])

  const handleRefresh = async (postId: number) => {
    setRefreshingId(postId)
    try {
      await api.post(`/analytics/posts/${postId}/refresh`, {})
    } finally {
      setRefreshingId(null)
    }
  }

  const combinedReachChart = useMemo(() => {
    const p0Data = dataByPeriod[0]
    if (!p0Data) return { labels: [] as string[], datasets: [] }
    if (dataByPeriod.length <= 1) return applyAccountColors(p0Data.reach_trend)
    const p0Chart = applyAccountColors(p0Data.reach_trend)
    return {
      labels: p0Chart.labels,
      datasets: dataByPeriod.flatMap((data, pi) => {
        if (!data || pi >= periods.length) return []
        const chart = applyAccountColors(data.reach_trend)
        const prefix = `${fmtDateRange(periods[pi])} | `
        return chart.datasets.map((ds, di) => {
          const c = getAccountChartColor(pi * p0Chart.datasets.length + di)
          return { ...ds, label: `${prefix}${ds.label}`, borderColor: c.border, backgroundColor: c.bg }
        })
      }),
    }
  }, [dataByPeriod, periods])

  const combinedEngagementChart = useMemo(() => {
    const p0Data = dataByPeriod[0]
    if (!p0Data) return { labels: [] as string[], datasets: [] }
    if (dataByPeriod.length <= 1) return applyAccountColors(p0Data.engagement_trend)
    const p0Chart = applyAccountColors(p0Data.engagement_trend)
    return {
      labels: p0Chart.labels,
      datasets: dataByPeriod.flatMap((data, pi) => {
        if (!data || pi >= periods.length) return []
        const chart = applyAccountColors(data.engagement_trend)
        const prefix = `${fmtDateRange(periods[pi])} | `
        return chart.datasets.map((ds, di) => {
          const c = getAccountChartColor(pi * p0Chart.datasets.length + di)
          return { ...ds, label: `${prefix}${ds.label}`, borderColor: c.border, backgroundColor: c.bg }
        })
      }),
    }
  }, [dataByPeriod, periods])

  const fmt = (v: number | null) => v != null ? v.toLocaleString() : "—"

  const data0 = dataByPeriod[0]

  return (
    <AppLayout pageTitle={PAGE_TITLES["/feed-posts"]}>
      <div className="analytics">
        {freeWindowAccount && <FreeWindowNotice days={freeWindowAccount.features.free_window_days} />}
        {selectedAccountIds.length === 0 ? (
          <AccountSelectionNotice />
        ) : (
          <>
            <div className="analytics__header">
              <PeriodSelectorModal
                periods={periods}
                onChange={setPeriods}
                storageKey={STORAGE_KEY}
                allTimeStart={earliestDate}
                maxDays={freeWindowAccount?.features.free_window_days}
              />
            </div>

            <div className="filter-tabs">
              {FILTER_LABELS.map(({ key, label }) => (
                <button
                  key={key}
                  className={`filter-tabs__item${filter === key ? " filter-tabs__item--active" : ""}`}
                  onClick={() => setFilter(key)}
                >
                  {label}
                </button>
              ))}
            </div>

            {loading ? (
              <p style={{ color: colors.gray400 }}>読み込み中…</p>
            ) : !data0 ? (
              <p style={{ color: colors.gray400 }}>データを取得できませんでした</p>
            ) : (
              <>
                {/* StatCards - メイン期間のみ */}
                <div className="analytics__stats-grid">
                  <StatCard label="投稿数"             stat={data0.stats.posts}          unit="件" />
                  <StatCard label="いいね"             stat={data0.stats.likes} />
                  <StatCard label="コメント"            stat={data0.stats.comments} />
                  <StatCard label="プロフィールアクセス" stat={data0.stats.profile_visits} />
                </div>

                {/* チャート - 全期間を1チャートに統合 */}
                <div className="analytics__section">
                  <div className="analytics__charts-grid">
                    {combinedReachChart.labels.length > 0 && (
                      <div className="chart-card">
                        <div className="chart-card__header">
                          <span className="chart-card__title">リーチ推移</span>
                          <div className="chart-card__actions">
                            <button className="summary-table__csv-btn" onClick={() => downloadCsv(chartDataToCsvRows(combinedReachChart), `feed_reach_trend_${todayStr()}.csv`)}>↓ CSV</button>
                            <button className="summary-table__csv-btn" onClick={() => downloadChartAsPng(reachChartRef.current, `feed_reach_trend_${todayStr()}.png`)}>↓ PNG</button>
                          </div>
                        </div>
                        <div className="chart-card__canvas-wrap" ref={reachChartRef}>
                          <LineChart data={combinedReachChart} dateAxis />
                        </div>
                      </div>
                    )}
                    {combinedEngagementChart.labels.length > 0 && (
                      <div className="chart-card">
                        <div className="chart-card__header">
                          <span className="chart-card__title">エンゲージメント推移</span>
                          <div className="chart-card__actions">
                            <button className="summary-table__csv-btn" onClick={() => downloadCsv(chartDataToCsvRows(combinedEngagementChart), `feed_engagement_trend_${todayStr()}.csv`)}>↓ CSV</button>
                            <button className="summary-table__csv-btn" onClick={() => downloadChartAsPng(engagementChartRef.current, `feed_engagement_trend_${todayStr()}.png`)}>↓ PNG</button>
                          </div>
                        </div>
                        <div className="chart-card__canvas-wrap" ref={engagementChartRef}>
                          <LineChart data={combinedEngagementChart} dateAxis />
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* サマリーテーブル - メイン期間のみ */}
                <div className="analytics__section">
                  <SummaryTable
                    rows={data0.summary}
                    columns={SUMMARY_COLUMNS}
                    title="サマリー"
                    filePrefix="feed_summary"
                  />
                </div>

                {/* ベスト・改善投稿 - メイン期間のみ */}
                <div className="analytics__charts-grid">
                  <div className="top-posts">
                    <div className="top-posts__header">
                      <span className="top-posts__title">ベスト投稿 TOP5（ENG率）</span>
                    </div>
                    {data0.best_posts?.length > 0
                      ? <PostGrid posts={applyPostColors(data0.best_posts)} aspect="square" onPostClick={(id, accountId) => navigate(`/feed-posts/${id}`, { state: { accountId } })} />
                      : <div className="top-posts__empty">データ収集中です</div>
                    }
                  </div>
                  <div className="top-posts">
                    <div className="top-posts__header">
                      <span className="top-posts__title">改善が必要な投稿 TOP5（ENG率）</span>
                    </div>
                    {data0.worst_posts?.length > 0
                      ? <PostGrid posts={applyPostColors(data0.worst_posts)} aspect="square" onPostClick={(id, accountId) => navigate(`/feed-posts/${id}`, { state: { accountId } })} />
                      : <div className="top-posts__empty">データ収集中です</div>
                    }
                  </div>
                </div>

                {/* 投稿一覧 - メイン期間のみ */}
                <div className="analytics__section">
                  <div className="posts-table">
                    <div className="posts-table__header">
                      <span className="posts-table__title">投稿一覧</span>
                      <button
                        className="summary-table__csv-btn"
                        onClick={() => downloadCsv(
                          [
                            ["キャプション", "ユーザー名", "投稿日時", "いいね", "コメント", "保存", "シェア", "リーチ", "ENG率(%)"],
                            ...data0.posts.map((p: FeedPostRow) => [
                              p.caption ?? "", p.username ?? "", p.posted_at ?? "",
                              p.likes, p.comments, p.saves, p.shares, p.reach, p.engagement_rate,
                            ]),
                          ],
                          `feed_posts_${todayStr()}.csv`
                        )}
                      >
                        ↓ CSV
                      </button>
                    </div>
                    <div className="posts-table__wrap">
                      <table>
                        <thead>
                          <tr>
                            <th>投稿</th>
                            <th>いいね</th>
                            <th>コメント</th>
                            <th>保存</th>
                            <th>シェア</th>
                            <th>リーチ</th>
                            <th>ENG率</th>
                            <th></th>
                          </tr>
                        </thead>
                        <tbody>
                          {data0.posts.map((post: FeedPostRow) => (
                            <tr key={post.id} className="posts-table__row--clickable" onClick={() => navigate(`/feed-posts/${post.id}`, { state: { accountId: post.instagram_account_id } })}>
                              <td>
                                <div className="posts-table__thumb-cell">
                                  {post.thumbnail_url
                                    ? <img src={post.thumbnail_url} alt="" className="posts-table__thumbnail" />
                                    : <div className="posts-table__no-thumb">📷</div>
                                  }
                                  <div className="posts-table__thumb-meta">
                                    {post.username && (
                                      <span
                                        className="posts-table__username"
                                        style={{ background: accountColorMap[post.username] ?? colors.gray400 }}
                                      >
                                        @{post.username}
                                      </span>
                                    )}
                                    <span className="posts-table__caption">{post.caption ?? "—"}</span>
                                  </div>
                                </div>
                              </td>
                              <td className={post.likes == null ? "null-value" : ""}>{fmt(post.likes)}</td>
                              <td className={post.comments == null ? "null-value" : ""}>{fmt(post.comments)}</td>
                              <td className={post.saves == null ? "null-value" : ""}>{fmt(post.saves)}</td>
                              <td className={post.shares == null ? "null-value" : ""}>{fmt(post.shares)}</td>
                              <td className={post.reach == null ? "null-value" : ""}>{fmt(post.reach)}</td>
                              <td className={post.engagement_rate == null ? "null-value" : ""}>
                                {post.engagement_rate != null ? `${post.engagement_rate}%` : "—"}
                              </td>
                              <td>
                                {post.can_refresh && (
                                  <button
                                    className="posts-table__refresh-btn"
                                    disabled={refreshingId === post.id}
                                    onClick={e => { e.stopPropagation(); handleRefresh(post.id) }}
                                  >
                                    {refreshingId === post.id ? "…" : "更新"}
                                  </button>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </>
            )}
          </>
        )}
      </div>
    </AppLayout>
  )
}

export default FeedPostsPage
